/**
 * Lock Screen Modal
 * 
 * Shows a full-screen login overlay when user is inactive.
 * Uses the Framer Home_Login component for consistent design.
 * Supports:
 * - Email/password login
 * - Biometric/PIN authentication via WebAuthn
 * - "Save Login" checkbox for credential persistence
 * - Multi-account support (foundation)
 */

"use client";

import { useEffect, useState, useCallback, useId, useLayoutEffect, useRef, type KeyboardEvent } from "react";
import { usePathname } from "next/navigation";
import { motion, AnimatePresence } from "framer-motion";
import { signIn } from "next-auth/react";
import { useSessionActivity, type SessionLockLevel } from "@/contexts/session-activity-context";
import { useDevMode, useIsPWA } from "@/contexts/dev-mode-context";
import { HomeLoginInteractive } from "./HomeLoginInteractive";
import { useSaveLogin } from "@/hooks/use-save-login";
import { useModalFocusManagement } from "@/hooks/useModalFocusManagement";
import { getDeviceFingerprint } from "@/lib/utils/device-fingerprint";
import {
  getWebAuthnCapabilities,
  registerWebAuthnCredential,
  authenticateWithWebAuthn,
  rememberAccount,
  getRememberedAccounts,
  type WebAuthnCapabilities,
} from "@/lib/auth/webauthn-client";

// =============================================
// Types
// =============================================

interface LockScreenModalProps {
  /** Callback when user successfully authenticates */
  onUnlock?: () => void;
}

interface RememberedAccount {
  userId: string;
  displayName: string | null;
  email: string | null;
  avatarUrl: string | null;
  hasPasskey: boolean;
  hasBiometric: boolean;
  lastUsedAt: string | null;
}

// =============================================
// Lock Level Messages (for potential future use)
// =============================================

const LOCK_MESSAGES: Record<SessionLockLevel, { title: string; description: string }> = {
  none: { title: "", description: "" },
  soft: {
    title: "Session Locked",
    description: "You've been inactive for a while. Sign in to continue where you left off.",
  },
  brand: {
    title: "Welcome Back",
    description: "Your session has been paused. Sign in to return to your brand.",
  },
  user: {
    title: "Welcome Back",
    description: "Sign in to view your brands and continue.",
  },
};

const STRINGS = {
  noBiometricCredentials: "No biometric credentials found",
  quickAuthFailed: "Quick authentication failed",
  quickAuthPasswordFallback: "Quick authentication failed. Please try password.",
  missingCredentials: "Please enter both email and password",
  unexpectedError: "An unexpected error occurred. Please try again.",
  biometricButton: "Sign in with biometrics",
  verifyingBiometrics: "Verifying biometrics...",
} as const;

// =============================================
// Component
// =============================================

export function LockScreenModal({ onUnlock }: LockScreenModalProps) {
  const pathname = usePathname();
  const { isLocked, lockLevel, unlock } = useSessionActivity();
  const { isPhoneMockupMode, isLoaded } = useDevMode();
  const isPWA = useIsPWA();
  
  // Form state
  const [scale, setScale] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const titleId = useId();
  const descriptionId = useId();
  const isOpen = isLoaded && isLocked && pathname !== '/';
  const lockMessage = LOCK_MESSAGES[lockLevel];

  useLayoutEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      returnFocusRef.current = document.activeElement instanceof HTMLElement
        && document.activeElement !== document.body
        ? document.activeElement
        : null;
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);

  const getReturnFocusElement = useCallback(() => {
    if (returnFocusRef.current?.isConnected) {
      return returnFocusRef.current;
    }

    return Array.from(document.querySelectorAll<HTMLElement>(
      'button:not([disabled]), a[href], input:not([disabled]), [tabindex="0"]',
    )).find((element) => !dialogRef.current?.contains(element)) ?? null;
  }, []);

  useModalFocusManagement({
    dialogRef,
    enabled: isOpen,
    getFallbackFocusElement: getReturnFocusElement,
    initialFocusRef: dialogRef,
    returnFocusElement: null,
  });
  
  // Save login state
  const { 
    isEnabled: isSaveLoginEnabled, 
    rememberedEmail,
    enableSaveLogin,
    disableSaveLogin,
    setRememberedEmail,
  } = useSaveLogin();
  
  // Device and WebAuthn state
  const [deviceFingerprint, setDeviceFingerprint] = useState<string>("");
  const [webAuthnCapabilities, setWebAuthnCapabilities] = useState<WebAuthnCapabilities | null>(null);
  const [rememberedAccounts, setRememberedAccounts] = useState<RememberedAccount[]>([]);
  const [showBiometricOption, setShowBiometricOption] = useState(false);
  const [isBiometricAuthenticating, setIsBiometricAuthenticating] = useState(false);

  // Initialize device fingerprint and check capabilities
  useEffect(() => {
    const initialize = async () => {
      try {
        // Get device fingerprint
        const fingerprint = await getDeviceFingerprint();
        setDeviceFingerprint(fingerprint);
        
        // Check WebAuthn capabilities
        const capabilities = await getWebAuthnCapabilities();
        setWebAuthnCapabilities(capabilities);
        
        // Fetch remembered accounts if save login is enabled
        if (isSaveLoginEnabled && fingerprint) {
          const { accounts } = await getRememberedAccounts(fingerprint);
          setRememberedAccounts(accounts);
          
          // If there's a remembered account with biometric, show that option
          const biometricAccount = accounts.find(a => a.hasBiometric || a.hasPasskey);
          if (biometricAccount && capabilities.isPlatformAuthenticatorAvailable) {
            setShowBiometricOption(true);
            // Pre-fill email from remembered account
            if (biometricAccount.email) {
              setEmail(biometricAccount.email);
            }
          } else if (rememberedEmail) {
            // Fall back to just pre-filling the email
            setEmail(rememberedEmail);
          }
        }
      } catch (err) {
        console.error("[LockScreenModal] Initialization failed:", err);
      }
    };
    
    if (isLocked) {
      initialize();
    }
  }, [isLocked, isSaveLoginEnabled, rememberedEmail]);

  // Handle email change from the interactive component
  const handleEmailChange = useCallback((newEmail: string) => {
    setEmail(newEmail);
    setError(null);
  }, []);

  // Handle password change from the interactive component
  const handlePasswordChange = useCallback((newPassword: string) => {
    setPassword(newPassword);
    setError(null);
  }, []);

  // Handle Save Login checkbox change
  const handleSaveLoginChange = useCallback(async (enabled: boolean) => {
    if (enabled) {
      enableSaveLogin(email || undefined);
    } else {
      // Disable save login and clear all data
      await disableSaveLogin();
      setRememberedAccounts([]);
      setShowBiometricOption(false);
    }
  }, [email, enableSaveLogin, disableSaveLogin]);

  // Handle biometric authentication
  const handleBiometricAuth = useCallback(async () => {
    if (!deviceFingerprint || rememberedAccounts.length === 0) return;
    
    setIsBiometricAuthenticating(true);
    setError(null);
    
    try {
      // Use the first remembered account with biometric support
      const account = rememberedAccounts.find(a => a.hasBiometric || a.hasPasskey);
      if (!account) {
        setError(STRINGS.noBiometricCredentials);
        setIsBiometricAuthenticating(false);
        return;
      }
      
      const result = await authenticateWithWebAuthn(deviceFingerprint, account.userId);
      
      if (!result.success) {
        setError(result.error || STRINGS.quickAuthFailed);
        // Fall back to password entry
        setShowBiometricOption(false);
        setIsBiometricAuthenticating(false);
        return;
      }
      
      // Sign in with NextAuth using the verified credential
      const signInResult = await signIn("credentials", {
        userId: account.userId,
        authMethod: "webauthn",
        redirect: false,
      });
      
      if (signInResult?.error) {
        setError(signInResult.error);
        setIsBiometricAuthenticating(false);
        return;
      }
      
      // Success - unlock and notify
      unlock();
      onUnlock?.();
      window.dispatchEvent(new CustomEvent("vastipost:auth:success"));
      
      // Update remembered account last used
      await rememberAccount(deviceFingerprint);
    } catch (err) {
      console.error("[LockScreenModal] Quick auth failed:", err);
      setError(STRINGS.quickAuthPasswordFallback);
      setShowBiometricOption(false);
    } finally {
      setIsBiometricAuthenticating(false);
    }
  }, [deviceFingerprint, rememberedAccounts, unlock, onUnlock]);

  // Handle login submission with password
  const handleSubmit = useCallback(async () => {
    if (!email || !password) {
      setError(STRINGS.missingCredentials);
      return;
    }

    setIsLoggingIn(true);
    setError(null);

    try {
      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.error) {
        setError(result.error);
        setIsLoggingIn(false);
        return;
      }

      // Login successful
      
      // If save login is enabled, remember the account and optionally register WebAuthn
      if (isSaveLoginEnabled && deviceFingerprint) {
        try {
          // Update remembered email
          setRememberedEmail(email);
          
          // Remember the account
          await rememberAccount(deviceFingerprint);
          
          // If WebAuthn is available and user doesn't have a credential yet,
          // offer to register one (biometric/PIN)
          if (webAuthnCapabilities?.isPlatformAuthenticatorAvailable) {
            const existingAccount = rememberedAccounts.find(a => a.email === email);
            
            if (!existingAccount?.hasBiometric) {
              // Register a WebAuthn credential for future biometric login
              const regResult = await registerWebAuthnCredential(
                deviceFingerprint,
                `${email} on this device`
              );
              
              if (regResult.success) {
                console.log("[LockScreenModal] WebAuthn credential registered");
                // Update remembered account to reflect biometric availability
                await rememberAccount(deviceFingerprint);
              } else {
                // Registration failed - not critical, user can still use password
                console.warn("[LockScreenModal] WebAuthn registration failed:", regResult.error);
              }
            }
          }
        } catch (saveErr) {
          console.error("[LockScreenModal] Failed to save login:", saveErr);
          // Non-critical - continue with login
        }
      }

      // Success - unlock and notify
      unlock();
      onUnlock?.();
      window.dispatchEvent(new CustomEvent("vastipost:auth:success"));
      
      // Clear form
      setEmail("");
      setPassword("");
    } catch (err) {
      setError(STRINGS.unexpectedError);
      setIsLoggingIn(false);
    }
  }, [
    email, 
    password, 
    unlock, 
    onUnlock, 
    isSaveLoginEnabled, 
    deviceFingerprint, 
    webAuthnCapabilities,
    rememberedAccounts,
    setRememberedEmail,
  ]);

  // Calculate scale for responsive mode
  useEffect(() => {
    const calculateScale = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scaleX = viewportWidth / 393;
      const scaleY = viewportHeight / 851;
      setScale(Math.min(scaleX, scaleY, 1.2));
    };

    calculateScale();
    window.addEventListener('resize', calculateScale);
    return () => window.removeEventListener('resize', calculateScale);
  }, []);

  // Listen for login success events from the Framer component
  useEffect(() => {
    if (!isLocked) return;

    const handleAuthSuccess = () => {
      unlock();
      onUnlock?.();
    };

    window.addEventListener("vastipost:auth:success", handleAuthSuccess);

    return () => {
      window.removeEventListener("vastipost:auth:success", handleAuthSuccess);
    };
  }, [isLocked, unlock, onUnlock]);

  const handleLockKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key !== "Escape") return;

    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Don't render on login page (it has its own auth handling) or until dev mode is loaded
  if (!isOpen) return null;

  // Determine if we should use responsive layout
  const useResponsiveLayout = isPWA || !isPhoneMockupMode;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="lock-screen-overlay"
        onKeyDown={handleLockKeyDown}
        className={
          useResponsiveLayout
            ? "fixed inset-0 z-50 flex items-center justify-center bg-[rgb(249,250,251)]"
            : "fixed inset-0 z-50 flex items-center justify-center bg-black/60"
        }
      >
        <motion.div
          ref={dialogRef}
          aria-busy={isLoggingIn || isBiometricAuthenticating}
          aria-describedby={descriptionId}
          aria-labelledby={titleId}
          aria-modal="true"
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          data-testid="lock-screen-dialog"
          role="dialog"
          tabIndex={-1}
          className="relative bg-[rgb(249,250,251)] rounded-[40px] overflow-hidden"
          style={{
            width: 393,
            height: 851,
            ...(useResponsiveLayout ? {
              transform: `scale(${scale})`,
            } : {}),
          }}
        >
          <h2 id={titleId} className="sr-only">{lockMessage.title}</h2>
          <p id={descriptionId} className="sr-only">{lockMessage.description}</p>

          {/* Framer Home_Login Component with Interactive Inputs */}
          <HomeLoginInteractive 
            email={email}
            password={password}
            onEmailChange={handleEmailChange}
            onPasswordChange={handlePasswordChange}
            onSubmit={handleSubmit}
            onSaveLoginChange={handleSaveLoginChange}
            initialSaveLogin={isSaveLoginEnabled}
            initialFocusField="email"
            skipAppearAnimation={true}
            style={{ width: 393, height: 851 }}
          />
          
          {/* Biometric authentication option */}
          {showBiometricOption && !isBiometricAuthenticating && (
            <div className="absolute top-[280px] left-1/2 -translate-x-1/2 z-10">
              <button
                type="button"
                onClick={handleBiometricAuth}
                className="flex items-center gap-2 px-4 py-2 bg-[#5ba9e1] text-white rounded-lg text-sm font-medium hover:bg-[#4a98d0] transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
                </svg>
                {STRINGS.biometricButton}
              </button>
            </div>
          )}
          
          {/* Error display */}
          {error && (
            <div role="alert" className="absolute bottom-24 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm max-w-[350px] text-center z-10">
              {error}
            </div>
          )}
          
          {/* Loading overlay */}
          {(isLoggingIn || isBiometricAuthenticating) && (
            <div role="status" aria-live="polite" className="absolute inset-0 bg-white/50 flex items-center justify-center z-20">
              <div className="flex flex-col items-center gap-2">
                <div aria-hidden="true" className="w-8 h-8 border-2 border-[#5ba9e1] border-t-transparent rounded-full animate-spin" />
                {isBiometricAuthenticating && (
                  <p className="text-sm text-gray-600">{STRINGS.verifyingBiometrics}</p>
                )}
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default LockScreenModal;
